/**
 * @docs docs/features/website-navigation-shell
 */

/*----------------------------------
- MOBILE ACTION BAR
----------------------------------*/

import type { IconName } from '@uniquedomains/components/ui/icon';
import Icon from '@/client/components/ui/icon/Icon';

/*----------------------------------
- TYPES
----------------------------------*/

export type MobileActionBarAction = {
    label: string;
    /** Omit for an action that only runs `onClick`, such as opening the /domains filter sheet. */
    href?: string;
    onClick?: () => void;
    /** Opens in a new tab: WhatsApp, RSS and other destinations off the site. */
    external?: boolean;
    testId?: string;
    /** Left slot only: a leading glyph, such as the sliders of the /domains Filters action. */
    iconName?: IconName;
    /** Left slot only: a butter count chip after the label. Zero and undefined render nothing. */
    badge?: number;
};

export type MobileActionBarProps = {
    /** Left slot, contextual to the page. Omitted for a member, who has nothing to log in to. */
    secondary?: MobileActionBarAction | null;
    /** Right slot: the one action the page is built around. */
    primary: MobileActionBarAction;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const SECONDARY_CLASS =
    'inline-flex min-h-[52px] flex-none items-center justify-center gap-2 rounded-full border border-[#c9c8c2] bg-white px-[22px] text-[15px] font-semibold text-[#1b1d1e]';
const PRIMARY_CLASS =
    'flex min-h-[52px] min-w-0 flex-1 items-center justify-between gap-3 rounded-full bg-[#1b1d1e] py-[5px] pl-5 pr-[5px] text-[15px] font-semibold text-white';

/*----------------------------------
- HELPERS
----------------------------------*/

const externalProps = (action: MobileActionBarAction) =>
    action.external ? { target: '_blank', rel: 'noopener noreferrer' } : {};

const renderSecondaryContent = (action: MobileActionBarAction) => (
    <>
        {action.iconName ? <Icon name={action.iconName} size={17} strokeWidth={2} /> : null}
        {action.label}
        {action.badge ? (
            <span class="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-[#f2e97e] px-1.5 text-xs font-bold">
                {action.badge}
            </span>
        ) : null}
    </>
);

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The phone's action bar: fixed to the bottom edge so the page's main action stays in
 * thumb reach while the top bar holds only the lockup and the menu button. Hidden from
 * `lg` up, where the header pill carries the same actions. `data-mobile-action-bar`
 * is what shared.css keys the matching body padding on, so the bar never covers the footer.
 */
export default function MobileActionBar({ secondary, primary }: MobileActionBarProps) {
    return (
        // A `nav` landmark: the bar sits outside `header`, `main` and `footer`, so as a plain
        // `div` its links belonged to no region a screen reader user can jump to.
        <nav
            aria-label="Page actions"
            data-mobile-action-bar
            class="fixed inset-x-0 bottom-0 z-40 flex gap-2.5 border-t border-[rgba(27,29,30,0.08)] bg-[rgba(248,246,241,0.94)] px-4 pb-[max(10px,env(safe-area-inset-bottom))] pt-2.5 backdrop-blur-md lg:hidden"
        >
            {secondary ? (
                secondary.href ? (
                    <a
                        href={secondary.href}
                        onClick={secondary.onClick}
                        class={SECONDARY_CLASS}
                        data-testid={secondary.testId}
                        {...externalProps(secondary)}
                    >
                        {renderSecondaryContent(secondary)}
                    </a>
                ) : (
                    <button
                        type="button"
                        onClick={secondary.onClick}
                        class={SECONDARY_CLASS}
                        data-testid={secondary.testId}
                    >
                        {renderSecondaryContent(secondary)}
                    </button>
                )
            ) : null}
            <a
                href={primary.href}
                onClick={primary.onClick}
                class={PRIMARY_CLASS}
                data-testid={primary.testId}
                {...externalProps(primary)}
            >
                <span class="truncate">{primary.label}</span>
                <span
                    aria-hidden="true"
                    class="inline-flex h-[42px] w-[42px] flex-none items-center justify-center rounded-full bg-[#f2e97e] text-[#1b1d1e]"
                >
                    <Icon name="arrow-right" size={16} strokeWidth={2.2} />
                </span>
            </a>
        </nav>
    );
}
